import type { ImageWithPreview } from '@modules/page-builder/types';
import { compressSingleImage } from '@modules/page-builder/hooks/use-image-compression';

export type ImagesWithPreviewPromise = ImageWithPreview[] | Event;

/**
 * Compresses an image file using the shared compression utility.
 * Uses the same logic and options as useImageCompression hook.
 * Only compresses if the result is smaller than the original.
 */
const compressImageFile = async (file: File): Promise<File> => {
    const result = await compressSingleImage(file);
    return result.file;
};

const createPreviewImage = async (
    file: ImageWithPreview
): Promise<ImageWithPreview> => {
    // First compress the image if needed
    const compressedFile = await compressImageFile(file);
    // Cast to ImageWithPreview since we'll be adding preview properties
    const processedFile = compressedFile as ImageWithPreview;

    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(compressedFile);
        reader.onload = () => {
            const source = (reader.result || '') as string;
            processedFile.preview = source;

            const image = new Image();
            image.src = source;
            image.onload = () => {
                processedFile.height = image.height;
                processedFile.width = image.width;
                processedFile.orientation =
                    image.width > image.height ? 'landscape' : 'portrait';
                processedFile.aspectRatio =
                    image.height > 0 ? image.width / image.height : 0;
                resolve(processedFile);
            };
            image.onerror = (e) => {
                reject(e);
            };
        };
        reader.onerror = (e) => {
            reject(e);
        };
    });
};

export const createPreviewImages = async (
    files: File[]
): Promise<ImagesWithPreviewPromise> => {
    const promises = files.map((file: ImageWithPreview) => {
        return createPreviewImage(file);
    });

    return await Promise.all(promises).catch((err) => {
        return err;
    });
};
